function defineProductModal() {
  if (customElements.get('product-modal') || typeof ModalDialog === 'undefined') return;

  const AUTOPLAY_MS = 3000;
  const ZOOM_LEVELS = [1, 1.5, 2.5];
  const SWIPE_THRESHOLD = 50;

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
        this.zoomIndex = 0;
        this.isPlaying = false;
        this.thumbsVisible = false;
        this.autoplayTimer = null;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        this.dragStart = { x: 0, y: 0 };
        this.touchStart = { x: 0, y: 0 };
        this.boundHandlers = {};
      }

      connectedCallback() {
        if (!this.moved) {
          this.moved = true;
          const section = this.closest('.shopify-section');
          if (section) {
            this.dataset.section = section.id.replace('shopify-section-', '');
          }
          document.body.appendChild(this);
        }
        this.cacheElements();
        this.bindEvents();
      }

      cacheElements() {
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.viewport = this.querySelector('.product-media-modal__viewport');
        this.track = this.querySelector('.product-media-modal__track');
        this.thumbs = this.querySelector('.product-media-modal__thumbs');
        this.thumbsTrack = this.querySelector('.product-media-modal__thumbs-track');
        this.prevBtn = this.querySelector('.product-media-modal__nav--prev');
        this.nextBtn = this.querySelector('.product-media-modal__nav--next');
        this.slideshowBtn = this.querySelector('[data-modal-action="slideshow"]');
        this.progressBar = this.slideshowBtn?.querySelector('.product-media-modal__progress');
        this.zoomBtn = this.querySelector('[data-modal-action="zoom"]');
        this.fullscreenBtn = this.querySelector('[data-modal-action="fullscreen"]');
        this.thumbsBtn = this.querySelector('[data-modal-action="thumbs"]');
        this.closeBtn = this.querySelector('[data-modal-action="close"]');
      }

      bindEvents() {
        if (this.boundHandlers.click) return;

        // ModalDialog closes media-modal on any mouse click outside deferred-media.
        // Stop pointer events inside the dialog from bubbling to product-modal.
        this.boundHandlers.dialogPointerUp = (event) => {
          event.stopPropagation();
        };
        this.dialog?.addEventListener('pointerup', this.boundHandlers.dialogPointerUp);

        this.boundHandlers.click = this.onToolbarClick.bind(this);
        this.boundHandlers.keydown = this.onKeydown.bind(this);
        this.boundHandlers.fullscreenchange = this.onFullscreenChange.bind(this);
        this.boundHandlers.touchstart = this.onTouchStart.bind(this);
        this.boundHandlers.touchmove = this.onTouchMove.bind(this);
        this.boundHandlers.touchend = this.onTouchEnd.bind(this);
        this.boundHandlers.imageClick = this.onImageClick.bind(this);
        this.boundHandlers.imagePointerDown = this.onImagePointerDown.bind(this);
        this.boundHandlers.imagePointerMove = this.onImagePointerMove.bind(this);
        this.boundHandlers.imagePointerUp = this.onImagePointerUp.bind(this);
        this.boundHandlers.thumbClick = this.onThumbClick.bind(this);

        this.addEventListener('click', this.boundHandlers.click);
        this.addEventListener('keydown', this.boundHandlers.keydown);
        this.prevBtn?.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));
        this.nextBtn?.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
        this.viewport?.addEventListener('click', this.boundHandlers.imageClick);
        this.viewport?.addEventListener('pointerdown', this.boundHandlers.imagePointerDown);
        this.viewport?.addEventListener('pointermove', this.boundHandlers.imagePointerMove);
        this.viewport?.addEventListener('pointerup', this.boundHandlers.imagePointerUp);
        this.viewport?.addEventListener('pointercancel', this.boundHandlers.imagePointerUp);
        this.viewport?.addEventListener('touchstart', this.boundHandlers.touchstart, { passive: true });
        this.viewport?.addEventListener('touchmove', this.boundHandlers.touchmove, { passive: true });
        this.viewport?.addEventListener('touchend', this.boundHandlers.touchend);
        this.thumbsTrack?.addEventListener('click', this.boundHandlers.thumbClick);
        document.addEventListener('fullscreenchange', this.boundHandlers.fullscreenchange);
      }

      getSlides() {
        return Array.from(this.track?.querySelectorAll('.product-media-modal__slide:not(.product-media-modal__slide--hidden)') || []);
      }

      getThumbs() {
        return Array.from(this.thumbsTrack?.querySelectorAll('.product-media-modal__thumb:not(.product-media-modal__thumb--hidden)') || []);
      }

      getActiveSlide() {
        return this.getSlides()[this.currentIndex];
      }

      getActiveImage() {
        const slide = this.getActiveSlide();
        if (!slide || slide.dataset.mediaType !== 'image') return null;
        return slide.querySelector('img');
      }

      show(opener) {
        super.show(opener);
        this.refreshGallery();
        const mediaId = opener?.getAttribute('data-media-id');
        const slides = this.getSlides();
        const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        this.goToSlide(index >= 0 ? index : 0, false);
      }

      hide() {
        this.resetGalleryState();
        super.hide();
      }

      refreshGallery() {
        this.cacheElements();
        this.updateNavState();
      }

      onToolbarClick(event) {
        const button = event.target.closest('[data-modal-action]');
        if (!button || !this.contains(button)) return;

        const action = button.dataset.modalAction;
        if (action === 'close') {
          event.preventDefault();
          this.hide();
          return;
        }

        event.preventDefault();

        switch (action) {
          case 'zoom':
            this.iterateZoom();
            break;
          case 'slideshow':
            this.toggleSlideshow();
            break;
          case 'fullscreen':
            this.toggleFullscreen();
            break;
          case 'thumbs':
            this.toggleThumbs();
            break;
        }
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goToSlide(this.currentIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goToSlide(this.currentIndex + 1);
        } else if (event.key === 'Escape' && document.fullscreenElement) {
          document.exitFullscreen?.();
        }
      }

      onThumbClick(event) {
        const thumb = event.target.closest('.product-media-modal__thumb');
        if (!thumb || thumb.classList.contains('product-media-modal__thumb--hidden')) return;

        const slides = this.getSlides();
        const index = slides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.mediaId);
        if (index >= 0) this.goToSlide(index);
      }

      onImageClick(event) {
        const image = event.target.closest('.product-media-modal__slide img');
        if (!image || !image.closest('.product-media-modal__slide')?.classList.contains('is-active')) return;
        if (this.isDragging) return;
        this.iterateZoom();
      }

      onTouchStart(event) {
        const touch = event.changedTouches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      }

      onTouchMove(event) {
        if (!this.getActiveImage() || this.zoomIndex === 0) return;
        this.stopSlideshow();
      }

      onTouchEnd(event) {
        if (this.zoomIndex > 0) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStart.x;
        const deltaY = touch.clientY - this.touchStart.y;

        if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

        this.stopSlideshow();
        if (deltaX < 0) this.goToSlide(this.currentIndex + 1);
        else this.goToSlide(this.currentIndex - 1);
      }

      onImagePointerDown(event) {
        const image = this.getActiveImage();
        const clickedImage = event.target.closest('.product-media-modal__slide.is-active img');
        if (!image || clickedImage !== image) return;
        if (this.zoomIndex === 0) return;

        this.isDragging = true;
        this.dragStart = { x: event.clientX - this.panX, y: event.clientY - this.panY };
        this.getActiveSlide()?.classList.add('is-dragging');
        image.setPointerCapture?.(event.pointerId);
      }

      onImagePointerMove(event) {
        if (!this.isDragging || this.zoomIndex === 0) return;

        this.panX = event.clientX - this.dragStart.x;
        this.panY = event.clientY - this.dragStart.y;
        this.applyZoomTransform();
      }

      onImagePointerUp() {
        this.isDragging = false;
        this.getActiveSlide()?.classList.remove('is-dragging');
      }

      goToSlide(index, fromAutoplay = false) {
        const slides = this.getSlides();
        if (!slides.length) return;

        if (!fromAutoplay) this.stopSlideshow();

        if (index < 0) index = slides.length - 1;
        if (index >= slides.length) index = 0;

        this.currentIndex = index;
        this.resetZoom(false);

        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });

        this.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
        this.updateNavState();
        this.updateThumbs();
        this.loadActiveMedia();

        if (this.isPlaying) this.startProgress();
      }

      updateNavState() {
        const slides = this.getSlides();
        const hasMany = slides.length > 1;
        if (this.prevBtn) this.prevBtn.disabled = !hasMany;
        if (this.nextBtn) this.nextBtn.disabled = !hasMany;
      }

      updateThumbs() {
        const activeSlide = this.getActiveSlide();
        if (!activeSlide) return;

        this.getThumbs().forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.dataset.mediaId === activeSlide.dataset.mediaId);
          if (thumb.classList.contains('is-active')) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        });
      }

      loadActiveMedia() {
        const slide = this.getActiveSlide();
        if (!slide) return;

        const deferredMedia = slide.querySelector('deferred-media, product-model');
        if (!deferredMedia) return;

        const template = deferredMedia.querySelector('template');
        const templateContent = template ? template.content : null;

        if (
          deferredMedia.nodeName === 'DEFERRED-MEDIA' &&
          templateContent &&
          templateContent.querySelector('.js-youtube')
        ) {
          deferredMedia.loadContent();
        }
      }

      iterateZoom() {
        const slide = this.getActiveSlide();
        const image = this.getActiveImage();
        if (!slide || !image) return;

        this.stopSlideshow();
        this.zoomIndex = (this.zoomIndex + 1) % ZOOM_LEVELS.length;

        if (this.zoomIndex === 0) {
          this.resetZoom(false);
          return;
        }

        slide.classList.add('is-zoomed');
        this.zoomBtn?.classList.add('is-active');
        this.applyZoomTransform();
      }

      applyZoomTransform() {
        const image = this.getActiveImage();
        if (!image) return;

        const scale = ZOOM_LEVELS[this.zoomIndex];
        image.style.transform = `scale(${scale}) translate(${this.panX / scale}px, ${this.panY / scale}px)`;
      }

      resetZoom(updateButton = true) {
        this.zoomIndex = 0;
        this.panX = 0;
        this.panY = 0;

        this.getSlides().forEach((slide) => {
          slide.classList.remove('is-zoomed', 'is-dragging');
          const image = slide.querySelector('img');
          if (image) {
            image.style.transform = '';
          }
        });

        if (updateButton) this.zoomBtn?.classList.remove('is-active');
      }

      toggleSlideshow() {
        if (this.isPlaying) {
          this.stopSlideshow();
          return;
        }

        if (this.getSlides().length < 2) return;

        this.isPlaying = true;
        this.slideshowBtn?.classList.add('is-playing');
        this.startProgress();
        this.scheduleAdvance();
      }

      startProgress() {
        if (!this.progressBar) return;

        this.progressBar.style.transition = 'none';
        this.progressBar.style.transform = 'scaleX(0)';
        void this.progressBar.offsetWidth;
        this.progressBar.style.transition = `transform ${AUTOPLAY_MS}ms linear`;
        this.progressBar.style.transform = 'scaleX(1)';
      }

      scheduleAdvance() {
        this.clearAutoplay();
        this.autoplayTimer = window.setTimeout(() => {
          if (!this.isPlaying) return;
          this.goToSlide(this.currentIndex + 1, true);
          this.scheduleAdvance();
        }, AUTOPLAY_MS);
      }

      stopSlideshow() {
        this.isPlaying = false;
        this.clearAutoplay();
        this.slideshowBtn?.classList.remove('is-playing');

        if (this.progressBar) {
          this.progressBar.style.transition = 'none';
          this.progressBar.style.transform = 'scaleX(0)';
        }
      }

      clearAutoplay() {
        if (this.autoplayTimer) {
          window.clearTimeout(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }

      toggleFullscreen() {
        if (!this.dialog) return;

        if (!document.fullscreenElement) {
          this.dialog.requestFullscreen?.().catch(() => {});
        } else {
          document.exitFullscreen?.();
        }
      }

      onFullscreenChange() {
        const isFullscreen = document.fullscreenElement === this.dialog;
        this.fullscreenBtn?.classList.toggle('is-active', isFullscreen);
      }

      toggleThumbs() {
        if (!this.thumbs) return;

        this.thumbsVisible = !this.thumbsVisible;
        this.thumbs.hidden = !this.thumbsVisible;
        this.thumbsBtn?.classList.toggle('is-active', this.thumbsVisible);

        if (this.thumbsVisible) this.updateThumbs();
      }

      hideThumbs() {
        this.thumbsVisible = false;
        if (this.thumbs) this.thumbs.hidden = true;
        this.thumbsBtn?.classList.remove('is-active');
      }

      exitFullscreen() {
        if (document.fullscreenElement) {
          document.exitFullscreen?.();
        }
        this.fullscreenBtn?.classList.remove('is-active');
      }

      resetGalleryState() {
        this.stopSlideshow();
        this.resetZoom();
        this.hideThumbs();
        this.exitFullscreen();
        this.currentIndex = 0;
        if (this.track) this.track.style.transform = '';
        this.getSlides().forEach((slide) => slide.classList.remove('is-active'));
      }
    }
  );
}

defineProductModal();
document.addEventListener('DOMContentLoaded', defineProductModal);
